import React, { useMemo } from 'react';

import { Button } from '@infoods/lifebase-ui';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';

import classes from './UploadImageDropzone.module.scss';
import { getFileFormatsText, getFileRejectionMessage } from '../../../../utils/fileUtils';
import { getBytesFromMb } from '../../../../utils/mathUtils';

type UploadImageDropzoneProps = {
  onUpload: (acceptedFiles: File[]) => void;
  maxFileSizeMb?: number;
  acceptFormats?: string[];
  uploadDescription?: string;
};

const UploadImageDropzone = ({
  onUpload,
  maxFileSizeMb = 5,
  acceptFormats = ['.png', '.svg', '.jpeg', '.jpg'],
  uploadDescription,
}: UploadImageDropzoneProps) => {
  const maxSize = useMemo(() => getBytesFromMb(maxFileSizeMb), [maxFileSizeMb]);

  const {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    noClick: true,
    multiple: false,
    noKeyboard: true,
    maxSize,
    accept: { 'image/*': acceptFormats },
    onDrop: onUpload,
  });

  const isUploadError = isDragReject || fileRejections.length > 0;

  const dropzoneBoxClasses = classNames(classes.dropzoneBox, {
    [classes.dragging]: isDragActive && !isUploadError,
    [classes.error]: isUploadError,
  });

  const formatsText = getFileFormatsText(acceptFormats);

  const getErrorText = () => {
    const rejectedFile = fileRejections[0];

    if (!rejectedFile) {
      return 'Invalid file';
    }

    const [{ code }] = rejectedFile.errors;

    return getFileRejectionMessage(code, { formatsText, maxFileSizeMb });
  };

  return (
    <div
      {...getRootProps({
        className: dropzoneBoxClasses,
        'data-test-id': 'dropzone',
      })}
    >
      <input {...getInputProps()} />
      <div className={classes.infoBox}>
        <h6>{isUploadError ? getErrorText() : 'Click to Browse File or Drag & Drop'}</h6>
        <p>
          Supports formats: {formatsText} up to {maxFileSizeMb} MB.
        </p>
        {uploadDescription && <p>{uploadDescription}</p>}
      </div>
      <Button variant="contained" size="small" onClick={open}>
        Browse File
      </Button>
    </div>
  );
};

export { UploadImageDropzone };
